/**
 * MarketingVariantsLink — small inline link to the public marketing-
 * variants share page for the current event.
 *
 * Decision 2026-05-14 (option B): variants are PUBLIC at
 * `/match/:eventId/variants` on the tenant's own domain — no admin hop,
 * no auth wall. The marketing team and external agencies can read +
 * copy without login.
 *
 * Risk accepted: competitors can scrape. Mitigations come later
 * (rate-limit + robots.txt + HMAC-signed URLs in v2).
 *
 * Renders nothing if `eventId` is missing (e.g. a malformed package).
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Sportradar URN, e.g. "sr:sport_event:69653824". */
  eventId: string;
  /** Optional small layout tweak. Bottom-of-page footer (default) vs
   *  inline alongside other meta. */
  variant?: 'inline' | 'footer';
}

export function MarketingVariantsLink({ eventId, variant = 'footer' }: Props): ReactElement | null {
  const { t } = useTranslation();
  if (!eventId) return null;
  // Same-origin link — the variants page lives on the tenant's own
  // domain at /match/:eventId/variants. React-router intercepts but
  // a plain <a> works equally and is simpler — no router state needed
  // and the page can be linked from emails / messages naturally.
  const url = `/match/${encodeURIComponent(eventId)}/variants`;

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
    color: '#3730a3',
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    borderRadius: 999,
    letterSpacing: 0.2,
  };
  const footerStyle: React.CSSProperties = {
    ...baseStyle,
    margin: '24px auto',
    width: 'fit-content',
    display: 'flex',
  };
  const style = variant === 'footer' ? footerStyle : baseStyle;

  return (
    <a href={url} style={style} title={t('variants.linkTitle')}>
      <span aria-hidden="true">📣</span>
      <span>{t('variants.eyebrow')}</span>
      <span aria-hidden="true" style={{ opacity: 0.6 }}>›</span>
    </a>
  );
}
